import { ResidentsService } from '../../../core/services/residents/residents.service';
import { CartService } from '../../../core/services/cart/cart.service';
import {
  Component,
  EventEmitter,
  Input,
  LOCALE_ID,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { Cart, CartProduct } from '../../models/cart.model';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { Resident } from '../../models/resident.model';
import { MatIconModule } from '@angular/material/icon';
import { ProductCounterComponent } from '../product-card/product-counter/product-counter.component';
import { MatDialog } from '@angular/material/dialog';
import { InfoModalComponent } from '../info-modal/info-modal.component';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { sortBySortiment } from '../../utils';
import { PermissionsService } from '../../../core/services/permissions/permissions.service';
import { DepositType } from '../../../core/types/enums';
import { ProductsService } from '../../../core/services/products';
import { ZeroPriceGuardService } from '../../../core/services/zero-price-guard/zero-price-guard.service';
import {
  UNRESTRICTED_COUNTER,
  ZeroPriceCounterState,
} from '../../../core/services/zero-price-guard/zero-price.util';
import { Product } from '../../models/product.model';

@Component({
  selector: 'app-cart-products-table',
  templateUrl: './cart-products-table.component.html',
  styleUrls: ['./cart-products-table.component.scss'],
  imports: [
    CommonModule,
    MatTableModule,
    MatIconModule,
    ProductCounterComponent,
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
  ],
})
export class CartProductsTableComponent implements OnInit, OnChanges {
  @Input() dataSource: CartProduct[];
  @Input() residentId: number;
  @Input() packagingPrice: number;
  @Input() showDivider: boolean = false;
  @Input() hideHeaders: boolean = false;
  @Output() cartChanged = new EventEmitter<Cart>();
  displayedColumns: string[] = [
    'product-number',
    'product',
    'price',
    'sum',
    'quantity',
  ];
  /**
   * The full products behind the 0 EUR entries of this cart, keyed by id. A `CartProduct` does
   * not carry the promotional areas, so the quantity limit can only be applied to rows whose
   * product has been resolved — anything unresolved stays unrestricted, exactly as before.
   */
  private promotionalProducts = new Map<number, Product>();
  private requestedIds = new Set<number>();
  private rowStates = new Map<number, ZeroPriceCounterState>();

  constructor(
    private cartService: CartService,
    private dialog: MatDialog,
    public permissionsService: PermissionsService,
    private productsService: ProductsService,
    private zeroPriceGuardService: ZeroPriceGuardService
  ) {}

  ngOnInit(): void {
    if (this.permissionsService.canAddProductsToCart()) {
      this.displayedColumns.push('delete');
    }
    this.resolvePromotionalProducts();
  }

  ngOnChanges(): void {
    this.resolvePromotionalProducts();
  }

  /**
   * What the counter of one row is bound to. Returning a stored object rather than building
   * one per call keeps `canIncrease` at a stable identity across change detection, so the
   * counter's input does not churn on every pass; `maxQuantity` is refreshed by
   * `refreshRowLimits()` on the events that can actually change it.
   */
  zeroPriceState(cartProduct: CartProduct): ZeroPriceCounterState {
    return this.rowStates.get(cartProduct?.id) ?? UNRESTRICTED_COUNTER;
  }

  /**
   * Only 0 EUR articles can be promotional, so an ordinary cart resolves nothing and costs no
   * request at all.
   */
  private resolvePromotionalProducts(): void {
    const ids = (this.dataSource ?? [])
      .filter(product => Number(product?.price) === 0)
      .map(product => product.id)
      .filter(id => !this.requestedIds.has(id));

    if (ids.length === 0) {
      this.refreshRowLimits();
      return;
    }
    ids.forEach(id => this.requestedIds.add(id));

    this.productsService.getProductsByIds(ids).subscribe(response => {
      (response?.data ?? []).forEach(product => {
        this.promotionalProducts.set(product.id, product);
        this.rowStates.set(product.id, this.buildRowState(product.id));
      });
      this.refreshRowLimits();
    });
  }

  private buildRowState(productId: number): ZeroPriceCounterState {
    return {
      maxQuantity: null,
      // Reads the product at call time, so the closure survives every refresh.
      canIncrease: (currentValue: number, nextValue: number) =>
        this.zeroPriceGuardService.canIncrease({
          ...this.zeroPriceScope(productId),
          currentValue,
          nextValue,
        }),
    };
  }

  private refreshRowLimits(): void {
    this.rowStates.forEach((state, productId) => {
      state.maxQuantity = this.zeroPriceGuardService.maxQuantityFor(
        this.zeroPriceScope(productId)
      );
    });
  }

  private zeroPriceScope(productId: number) {
    return {
      product: this.promotionalProducts.get(productId) ?? null,
      resident: { id: this.residentId } as Resident,
    };
  }

  get sortedProducts(): CartProduct[] {
    const sortedBySortiment = [...this.dataSource].sort((a, b) =>
      sortBySortiment(a, b)
    );

    const productsWithoutDeposit: CartProduct[] = [];
    const productsWithDeposit: CartProduct[] = [];

    for (const product of sortedBySortiment) {
      const hasDeposit =
        (product.depositType ?? DepositType.NONE) !== DepositType.NONE;
      if (hasDeposit) {
        productsWithDeposit.push(product);
      } else {
        productsWithoutDeposit.push(product);
      }
    }

    return [...productsWithoutDeposit, ...productsWithDeposit];
  }

  get residentDeposit(): number {
    return this.cartService.cart.getResidentCart(this.residentId)?.deposit ?? 0;
  }

  hasDeposit(product: CartProduct): boolean {
    return (product?.depositType ?? DepositType.NONE) !== DepositType.NONE;
  }

  getDepositLabel(product: CartProduct): string {
    switch (product?.depositType) {
      case DepositType.EINWEG_0_25:
        return 'EINWEG';
      case DepositType.MEHRWEG_0_15:
        return 'MEHRWEG';
      case DepositType.MEHRWEG_0_08:
        return 'MEHRWEG';
      default:
        return '';
    }
  }

  get depositFooterColumns(): string[] {
    const columns = [
      'deposit-product-number',
      'deposit-product',
      'deposit-price',
      'deposit-sum',
      'deposit-quantity',
    ];

    if (this.permissionsService.canAddProductsToCart()) {
      columns.push('deposit-delete');
    }

    return columns;
  }

  get packagingFooterColumns(): string[] {
    const columns = [
      'packaging-product-number',
      'packaging-product',
      'packaging-price',
      'packaging-sum',
      'packaging-quantity',
    ];

    if (this.permissionsService.canAddProductsToCart()) {
      columns.push('packaging-delete');
    }

    return columns;
  }

  onCounterChange(event, product): void {
    let residentCart = this.cartService.cart.getResidentCart(this.residentId);
    this.cartService.setUpdate();
    if (!residentCart) {
      residentCart = this.cartService.cart.createNewResidentCart(
        this.residentId
      );
    }
    const productCounterEqualsZero = event.value === 0;
    if (productCounterEqualsZero) {
      this.cartService.cart.removeProductInResidentCart(
        this.residentId,
        product.id
      );
      return;
    }
    if (!residentCart.getProduct(product.id)) {
      this.cartService.cart.addProductToResidentCart(
        this.residentId,
        product.convertToCartProduct()
      );
    }

    this.cartService.cart.changeResidentCartProductQuantity(
      this.residentId,
      product.id,
      event.value
    );
    this.cartService.cart.total = this.cartService.cart.recalculateCartTotal();
    this.refreshRowLimits();
    this.cartChanged.emit(this.cartService.cart);
    this.cartService.setCart(this.cartService.cart);
  }

  getProductCount(product: CartProduct): number {
    if (
      this.cartService.cart.getResidentCart(this.residentId) &&
      this.cartService.cart
        .getResidentCart(this.residentId)
        .getProduct(product.id)
    )
      return this.cartService.cart
        .getResidentCart(this.residentId)
        .getProduct(product.id).quantity;

    return 0;
  }

  deleteProduct(productId: number) {
    this.cartService.setUpdate();
    this.cartService.cart
      .getResidentCart(this.residentId)
      .removeProduct(productId);

    this.dataSource = this.dataSource.filter(
      product => product.id !== productId
    );
    this.cartService.cart.total = this.cartService.cart.recalculateCartTotal();
    this.cartChanged.emit(this.cartService.cart);
    this.cartService.setCart(this.cartService.cart);
  }

  showDeleteModal(productId: number) {
    this.dialog.open(ConfirmationModalComponent, {
      width: 'auto',
      maxWidth: '540px',
      autoFocus: false,
      data: {
        message: 'Möchten Sie dieses Produkt wirklich aus der Tüte löschen?',
        closable: true,
        buttons: [
          { text: 'Nein', id: 0, closeDialog: true, color: '#42405F' },
          {
            text: 'Ja, löschen',
            id: 1,
            action: () => this.deleteProduct(productId),
            color: '#ff6100',
          },
        ],
      },
    });
  }

  counterClick(event: Event): void {
    event.stopPropagation();
  }
}
